import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { preauthStorage, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ShieldCheck, KeyRound, LogOut, LifeBuoy } from "lucide-react";

const LEN = 6;

export default function TwoFactorVerify() {
  const { user, verifyTotp, verifyBackup, logout } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(true);
  const [useBackup, setUseBackup] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) return;
    if (!preauthStorage.get()) {
      navigate("/login", { replace: true });
    }
  }, [navigate, user]);

  // Already signed in? Skip.
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    if (e) e.preventDefault();
    const value = useBackup ? code.trim().toLowerCase().replace(/[\s-]/g, "") : code.replace(/\D/g, "");
    if (!useBackup && value.length !== LEN) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    if (useBackup && value.length < 6) {
      setError("Enter one of your backup codes.");
      return;
    }
    setBusy(true); setError("");
    try {
      if (useBackup) await verifyBackup(value, trust);
      else await verifyTotp(value, trust);
      navigate("/", { replace: true });
    } catch (e) {
      setError(formatApiError(e) || "Verification failed.");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const onSignOut = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div data-testid="twofa-verify-page" className="min-h-screen bg-slate-950 text-white relative overflow-hidden flex items-center justify-center px-6">
      <div
        className="absolute inset-0 opacity-[0.22]"
        style={{ background: "radial-gradient(50% 60% at 50% 0%, rgba(249,115,22,0.5), transparent 60%), radial-gradient(40% 50% at 0% 100%, rgba(255,255,255,0.10), transparent 60%)" }}
      />
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <img src="/d4ps-logo.png" alt="D4PS" className="h-10 w-10 rounded-sm object-contain bg-black/40 p-1" />
          <div>
            <div className="font-display text-lg font-semibold tracking-tight">D4PS</div>
            <div className="text-[9px] tracking-[0.32em] text-slate-400 mt-0.5">COURIERS &amp; LOGISTICS</div>
          </div>
        </div>

        <div className="text-[11px] uppercase tracking-[0.32em] text-orange-400/95 font-semibold inline-flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-orange-500" />
          {useBackup ? "Backup code sign-in" : "Two-factor sign-in"}
        </div>
        <h1 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight">
          Verify your identity
        </h1>
        <div className="mt-3 h-[3px] w-12 bg-orange-500" />
        <p className="mt-4 text-slate-300 text-sm leading-relaxed">
          {useBackup
            ? "Enter one of your one-time backup codes. Each code can only be used once."
            : "Enter the 6-digit code shown in your authenticator app to continue."}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
              {useBackup ? "Backup code" : "Authenticator code"}
            </label>
            <Input
              data-testid="twofa-verify-code"
              inputMode={useBackup ? "text" : "numeric"}
              autoComplete="one-time-code"
              autoFocus
              maxLength={useBackup ? 32 : LEN}
              placeholder={useBackup ? "e.g. 5b451b05b4" : "000000"}
              value={code}
              onChange={(e) => setCode(useBackup ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, LEN))}
              className={`mt-2 h-12 rounded-sm bg-slate-900/70 border-slate-700 text-white text-center font-display text-xl ${useBackup ? "font-mono tracking-widest" : "tabular-nums tracking-[0.5em]"}`}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-300">
            <Checkbox
              data-testid="twofa-verify-trust"
              checked={trust}
              onCheckedChange={(v) => setTrust(!!v)}
              className="border-slate-500 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
            />
            Trust this device for 15 days
          </label>

          {error && (
            <div data-testid="twofa-verify-error" className="text-sm text-rose-300 bg-rose-950/40 border border-rose-900/60 px-3 py-2 rounded-sm">
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="twofa-verify-submit"
            disabled={busy}
            className="w-full h-11 rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-medium"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            Continue
          </Button>
        </form>

        <button
          type="button"
          data-testid="twofa-toggle-backup"
          onClick={() => { setUseBackup((v) => !v); setCode(""); setError(""); }}
          className="mt-4 inline-flex items-center gap-1.5 text-[12px] text-slate-400 hover:text-slate-200 underline-offset-2 hover:underline"
        >
          <LifeBuoy className="h-3 w-3" />
          {useBackup ? "Use authenticator app instead" : "Lost your device? Use a backup code"}
        </button>

        <div className="mt-10 pt-6 border-t border-slate-800/60 flex items-center justify-between text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1.5"><KeyRound className="h-3 w-3" /> Signed in via password</span>
          <button onClick={onSignOut} className="inline-flex items-center gap-1 hover:text-slate-300" data-testid="twofa-verify-signout">
            <LogOut className="h-3 w-3" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
